import { Link } from "react-router-dom";
import Button from "../../components/Button/Button";
import Checkmark from "../../components/Checkmark/Checkmark";
import { EssayPrizeCard } from "./EssayPrizeCard";
import type { EssayPrizeCategory } from "./essayPrizes";
import {
  ESSAY_PRIZE_CLOSING_DATE,
  ESSAY_PRIZE_HEAR_BACK_DATE,
  ESSAY_PRIZES_OVERVIEW_PATH,
  SUBMISSION_EMAIL,
  SUBMISSION_MAILTO,
} from "./essayPrizes";

function getSubmissionRequirements(category: EssayPrizeCategory) {
  return [
    { label: "Word count", value: "1,500 – 2,000 words (excluding references)" },
    { label: "Referencing style", value: "Academic (Vancouver or Harvard accepted)" },
    { label: "Format", value: "PDF or Word document (.docx)" },
    { label: "Eligibility", value: category.eligibility.join(" ") },
    {
      label: "How to submit",
      href: SUBMISSION_MAILTO,
      linkText: SUBMISSION_EMAIL,
    },
    { label: "Closing date", value: ESSAY_PRIZE_CLOSING_DATE },
    { label: "Hear back by", value: ESSAY_PRIZE_HEAR_BACK_DATE },
  ];
}

type CategoryPrizeDetailPanelProps = {
  category: EssayPrizeCategory;
  submitMailto?: string;
};

const CategoryPrizeDetailPanel = ({
  category,
  submitMailto = SUBMISSION_MAILTO,
}: CategoryPrizeDetailPanelProps) => {
  return (
    <EssayPrizeCard className="nep-prize-panel">
      <div className="nep-prize-panel__section nep-prize-panel__section--hero">
        <p className="nep-prize-card__amount">£500</p>
        <h2 className="nep-prize-card__title">{category.title}</h2>
        <p className="nep-prize-card__audience">{category.shortLabel}</p>

        <ul className="nep-prize-card__list">
          {category.eligibility.map((item) => (
            <li key={item} className="nep-prize-card__list-item">
              <Checkmark />
              <span>{item}</span>
            </li>
          ))}
        </ul>

        <div className="nep-essay-question">
          <p className="nep-essay-question__label">Essay question</p>
          <blockquote className="nep-essay-question__quote">
            &ldquo;{category.essayQuestion}&rdquo;
          </blockquote>
        </div>
      </div>

      <div className="nep-prize-panel__section nep-prize-panel__section--aside">
        <p className="nep-prize-panel__aside-box">
          Entering a different career stage?{" "}
          <Link to={ESSAY_PRIZES_OVERVIEW_PATH} className="nep-prize-panel__inline-link">
            View all prize categories →
          </Link>
        </p>
      </div>

      <div className="nep-prize-panel__section">
        <h3 className="nep-prize-panel__heading">Submission requirements</h3>
        <dl className="nep-prize-card__details">
          {getSubmissionRequirements(category).map((item) => (
            <div key={item.label} className="nep-prize-card__details-row">
              <dt>{item.label}</dt>
              <dd>
                {"linkText" in item && item.href ? (
                  <>
                    Email your essay manually to{" "}
                    <a href={submitMailto}>{item.linkText}</a> before the deadline
                  </>
                ) : (
                  "value" in item ? item.value : null
                )}
              </dd>
            </div>
          ))}
        </dl>
      </div>

      <div className="nep-prize-panel__section">
        <h3 className="nep-prize-panel__heading">Judging &amp; announcement</h3>
        <p className="nep-prize-card__body">
          Essays will be assessed by a panel of consultant ophthalmologists, academic clinicians, and senior
          trainees. Entries will be judged on originality of argument, quality of evidence-based reasoning, clarity
          of academic writing, and engagement with contemporary issues in ophthalmology.
        </p>
        <p className="nep-prize-card__body">
          You should hear back by <strong>{ESSAY_PRIZE_HEAR_BACK_DATE}</strong>. Results will be announced and the
          prize presented at a forthcoming national event.
        </p>
      </div>

      <div className="nep-prize-panel__section nep-prize-panel__section--cta" id="submit">
        <h3 className="nep-prize-panel__heading">Ready to enter?</h3>
        <p className="nep-prize-card__body">
          Send your submission manually by email to{" "}
          <a href={submitMailto}>{SUBMISSION_EMAIL}</a> before{" "}
          <strong>{ESSAY_PRIZE_CLOSING_DATE}</strong>. Attach your essay as a PDF or Word document (.docx). Each entrant may
          submit one essay per category. Co-authored submissions are not accepted; entries must be your original,
          unpublished work.
        </p>
        <p className="nep-prize-card__body nep-prize-card__body--muted">
          For enquiries regarding eligibility or submission, email{" "}
          <a href={submitMailto}>{SUBMISSION_EMAIL}</a>.
        </p>
        <div className="nep-prize-card__action">
          <Button text="Email your submission" linkTo={submitMailto} external type="navy" />
        </div>
      </div>
    </EssayPrizeCard>
  );
};

export default CategoryPrizeDetailPanel;
